import { useEffect, useMemo, useRef, useState } from 'react';
import { createWebSocket, type WsHandle } from '../../lib/websocket';
import type { ChatClientEvent, ChatStreamEvent } from '../../lib/types';
import { playTTS, recordAudio, transcribe, type RecordingHandle } from '../../lib/audio';
import { toast } from '../../lib/toast';
import { useChat } from './chatStore';
import { ChatMessageBubble } from './ChatMessageBubble';
import { Composer } from './Composer';
import styles from './Chat.module.css';

export function ChatPage() {
  const messages = useChat((s) => s.messages);
  const streaming = useChat((s) => s.streaming);
  const socketState = useChat((s) => s.socketState);
  const recording = useChat((s) => s.recording);

  const wsRef = useRef<WsHandle<ChatClientEvent> | null>(null);
  const recRef = useRef<RecordingHandle | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [partialTranscript, setPartialTranscript] = useState('');
  const [draft, setDraft] = useState('');

  // Open the chat WS once per mount. The reconnect logic lives in the helper.
  useEffect(() => {
    const handle = createWebSocket<ChatStreamEvent, ChatClientEvent>('/api/v1/chat/stream', {
      onState: (s) => useChat.getState().setSocketState(s),
      onMessage: (msg) => {
        if (msg.kind === 'transcript') {
          setPartialTranscript(msg.text);
          return;
        }
        if (msg.kind === 'error') {
          toast.error(msg.error);
        }
        useChat.getState().applyEvent(msg);
      },
      onError: () => {
        // Surface errors quietly; reconnect handler will retry.
      },
    });
    wsRef.current = handle;
    return () => {
      handle.close();
      wsRef.current = null;
    };
  }, []);

  // Auto-scroll to the latest message as it streams.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  function sendText(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!wsRef.current || wsRef.current.state() !== 'open') {
      toast.error('Соединение с сервером ещё не установлено.');
      return;
    }
    useChat.getState().pushUser(trimmed);
    useChat.getState().pushAssistantStub();
    wsRef.current.send({ kind: 'user_message', content: trimmed });
    setDraft('');
  }

  async function startRecording() {
    if (recRef.current) return;
    try {
      const handle = await recordAudio();
      recRef.current = handle;
      useChat.getState().setRecording(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Не удалось получить доступ к микрофону.',
      );
    }
  }

  async function stopRecording() {
    const handle = recRef.current;
    if (!handle) return;
    useChat.getState().setRecording(false);
    try {
      const blob = await handle.stop();
      // v1: upload-then-transcribe over HTTP. Streaming chunked transcription
      // can come later — Whisper handles ~25MB per request, plenty for this UX.
      const { text } = await transcribe(blob);
      if (text) {
        setPartialTranscript('');
        sendText(text);
      } else {
        toast.info('Не удалось распознать запись.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка распознавания.');
    } finally {
      recRef.current = null;
    }
  }

  function cancelRecording() {
    recRef.current?.cancel();
    recRef.current = null;
    useChat.getState().setRecording(false);
    setPartialTranscript('');
  }

  async function speak(text: string) {
    try {
      await playTTS(text);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Озвучка недоступна.');
    }
  }

  const empty = messages.length === 0;
  const wsLabel = useMemo(() => {
    switch (socketState) {
      case 'open':
        return 'на связи';
      case 'connecting':
        return 'соединение…';
      case 'closing':
        return 'разрыв…';
      case 'closed':
        return 'нет связи';
    }
  }, [socketState]);

  return (
    <main className={styles.page}>
      <div className={styles.head}>
        <div className={styles.left}>Беседа</div>
        <div className={styles.center}>
          <em>Линза</em>
        </div>
        <div className={styles.right}>
          <span className={`${styles.dot} ${styles[`dot_${socketState}`]}`} />
          {wsLabel}
        </div>
      </div>

      <div ref={scrollRef} className={styles.scroll}>
        {empty ? (
          <div className={styles.empty}>
            <div className={styles.eyebrow}>Утро · готов выслушать</div>
            <h1 className={styles.greeting}>
              С чего <em>начнём</em>?
            </h1>
            <p className={styles.lede}>
              Опишите встречу, опишите день, или нажмите микрофон и расскажите голосом.
              Я слушаю и веду тихую запись&nbsp;— только то, что вы попросите сохранить.
            </p>
          </div>
        ) : (
          <ol className={styles.thread}>
            {messages.map((m) => (
              <ChatMessageBubble
                key={m.id}
                message={m}
                onSpeak={() => speak(m.content)}
              />
            ))}
            {streaming && messages[messages.length - 1]?.role !== 'assistant' && (
              <li className={styles.thinking}>думает…</li>
            )}
          </ol>
        )}
      </div>

      <Composer
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={() => sendText(draft)}
        recording={recording}
        partialTranscript={partialTranscript}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onCancelRecording={cancelRecording}
        disabled={streaming}
      />
    </main>
  );
}
